import React, { useState } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Typography,
    IconButton,
    TextField,
    Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SubjectIcon from '@mui/icons-material/Subject';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { Section, ApplicationQuestion } from '../../../../types/Applications';
import SingleQuestionPanel from './SingleQuestionPanel';

interface SectionPanelProps {
    section: Section;
    questions: ApplicationQuestion[];
    onSectionTitleUpdate?: (sectionId: string, newTitle: string) => void;
    onEditQuestion?: (questionId: string) => void;
    onDeleteQuestion?: (questionId: string) => void;
    onAddQuestion?: (sectionId: string) => void;
}

const SectionPanel: React.FC<SectionPanelProps> = ({
    section,
    questions,
    onSectionTitleUpdate,
    onEditQuestion,
    onDeleteQuestion,
    onAddQuestion
}) => {
    const [expanded, setExpanded] = useState<boolean>(true);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editedTitle, setEditedTitle] = useState<string>(section.title);

    console.log('SectionPanel questions:', {
        questions,
    });

    const handleAccordionChange = () => {
        if (!isEditing) {
            setExpanded(!expanded);
        }
    };

    const handleEditClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        setIsEditing(true);
        setEditedTitle(section.title);
    };



    const handleCancelClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        setIsEditing(false);
        setEditedTitle(section.title);
    };

    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEditedTitle(event.target.value);
    };

    const handleTitleBlur = () => {
        const trimmedValue = editedTitle.trim();
        if (trimmedValue !== '' && trimmedValue !== section.title && onSectionTitleUpdate) {
            onSectionTitleUpdate(section.id, trimmedValue);
        } else if (trimmedValue === '') {
            // Reset to original title if empty
            setEditedTitle(section.title);
        }

        // Exit editing mode when field loses focus
        setIsEditing(false);
    };

    return (
        <Accordion
            expanded={expanded}
            onChange={handleAccordionChange}
            sx={{
                mb: 2,
                borderRadius: 2,
                overflow: 'hidden',
                '&:before': {
                    display: 'none',
                },
                boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)'
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`section-${section.id}-content`}
                id={`section-${section.id}-header`}
                sx={{
                    backgroundColor: 'background.paper',
                    borderBottom: expanded ? '1px solid' : 'none',
                    borderColor: 'divider'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <SubjectIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                    {isEditing ? (
                        <TextField
                            value={editedTitle}
                            onChange={handleTitleChange}
                            onBlur={handleTitleBlur}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                            fullWidth
                            size="small"
                            sx={{ mr: 2 }}
                            inputProps={{
                                sx: { fontWeight: 500, fontSize: '1.25rem' }
                            }}
                        />
                    ) : (
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            {section.title}
                        </Typography>
                    )}

                    <Box sx={{ ml: 'auto', display: 'flex' }}>
                        {isEditing ? (
                            <Tooltip title="Cancel">
                                <IconButton
                                    size="small"
                                    onClick={handleCancelClick}
                                    sx={{ ml: 0.5 }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <>
                                <Tooltip title="Add question to this section">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onAddQuestion && onAddQuestion(section.id);
                                        }}
                                    >
                                        <AddIcon fontSize="small" color="primary" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit section title">
                                    <IconButton
                                        size="small"
                                        onClick={handleEditClick}
                                        sx={{ ml: 1 }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </Box>
                </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 3, pb: 2, backgroundColor: 'background.paper' }}>
                {questions.length > 0 ? (
                    questions.map(question => (
                        <SingleQuestionPanel
                            key={question.id}
                            question={question}
                            onEditQuestion={onEditQuestion}
                            onDeleteQuestion={onDeleteQuestion}
                        />
                    ))
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        No questions in this section yet.
                    </Typography>
                )}

                {/* Add a small spacing at the bottom */}
                <Box sx={{ mt: 2 }}></Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default SectionPanel;